import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import {
	IGetSiteAccessesThunkArgs,
	ISiteAccess,
	ICreateSiteAccessThunkArgs,
	IGetPatientCreatorsInSiteThunkArgs,
	IUpdateSiteAccessThunkArgs,
	ISiteAccessShortInfo,
	IGetSiteAccessesShortInfoThunkArgs,
} from 'src/features/site_access/domain/types';
import { siteAccessApi } from 'src/features/site_access/infrastructure/api';
import { IUserOption } from 'src/features/user';
import { handleThunkError } from 'src/utils/handleThunkError';

export const SITE_ACCESS_LOADING_KEY = 'site_access';

export const getSiteAccesses = createAsyncThunk<
	IPaginatedResponse<ISiteAccess>,
	IGetSiteAccessesThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'site_access/getSiteAccesses',
	async (
		{ request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({ key: SITE_ACCESS_LOADING_KEY, loading: true }),
			);
			const response = await siteAccessApi.getSiteAccesses(
				request,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch site accesses',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({ key: SITE_ACCESS_LOADING_KEY, loading: false }),
			);
		}
	},
);

export const createSiteAccess = createAsyncThunk<
	ISiteAccess,
	ICreateSiteAccessThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'site_access/createSiteAccess',
	async (
		{ request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({ key: SITE_ACCESS_LOADING_KEY, loading: true }),
			);
			const response = await siteAccessApi.createSiteAccess(
				request,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to create site access');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({ key: SITE_ACCESS_LOADING_KEY, loading: false }),
			);
		}
	},
);

export const getPatientCreatorsInSite = createAsyncThunk<
	IUserOption[],
	IGetPatientCreatorsInSiteThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'site_access/getPatientCreatorsInSite',
	async ({ siteSlug, onSuccess, onError }, { dispatch, rejectWithValue }) => {
		try {
			dispatch(
				setLoading({ key: SITE_ACCESS_LOADING_KEY, loading: true }),
			);
			const response =
				await siteAccessApi.getPatientCreatorsInSite(siteSlug);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch patient creators in site',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({ key: SITE_ACCESS_LOADING_KEY, loading: false }),
			);
		}
	},
);

export const updateSiteAccess = createAsyncThunk<
	ISiteAccess,
	IUpdateSiteAccessThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'site_access/updateSiteAccess',
	async (
		{ request, siteSlug, siteAccessId, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({ key: SITE_ACCESS_LOADING_KEY, loading: true }),
			);
			const response = await siteAccessApi.updateSiteAccess(
				request,
				siteSlug,
				siteAccessId,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to update site access');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({ key: SITE_ACCESS_LOADING_KEY, loading: false }),
			);
		}
	},
);

export const getSiteAccessesShortInfo = createAsyncThunk<
	ISiteAccessShortInfo[],
	IGetSiteAccessesShortInfoThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'site_access/getSiteAccessesShortInfo',
	async (
		{ siteSlug, requestParams, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({ key: SITE_ACCESS_LOADING_KEY, loading: true }),
			);
			const response = await siteAccessApi.getSiteAccessesShortInfo(
				siteSlug,
				requestParams,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch site accesses short info',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({ key: SITE_ACCESS_LOADING_KEY, loading: false }),
			);
		}
	},
);
